import { motion, AnimatePresence } from 'framer-motion';

interface EmailConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Modal shown after successful email confirmation
 */
export function EmailConfirmationModal({ isOpen, onClose }: EmailConfirmationModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.3 }}
            className="relative bg-gradient-to-br from-gray-900 to-gray-800 border border-green-500/20 rounded-2xl p-8 max-w-md w-full shadow-2xl"
          >
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                <svg 
                  className="w-10 h-10 text-green-400" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M5 13l4 4L19 7" 
                  />
                </svg>
              </div>
            </div>

            {/* Content */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-3">
                Email Confirmed!
              </h2>
              <p className="text-gray-400 text-base leading-relaxed">
                Your account has been successfully verified. You can now access all features and start sharing files securely.
              </p>
            </div>

            {/* Button */}
            <button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-medium py-3 px-4 rounded-xl transition-all shadow-lg hover:shadow-xl"
            >
              Get Started
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
